import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import QRCode from 'react-qr-code';
import { ErrorMessage } from '../styled/ErrorMessage';
import { useTranslation } from "react-i18next";
import i18n from '../i18n';
import axios from 'axios';
import { BsFillClipboard2PlusFill, BsFillEnvelopeAtFill } from 'react-icons/bs';
import { ClipLoader } from 'react-spinners';
import { fetchLanguages } from "../context/LanguageUtility";

const ModalBackground = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.6);
    backdrop-filter: blur(4px);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 10;
`;

const ModalContent = styled.div`
    background: rgba(30, 27, 75, 0.95);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 20px;
    backdrop-filter: blur(16px);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
    padding: 2em;
    max-width: 460px;
    width: 90%;
    position: relative;
    color: rgba(255, 255, 255, 0.85);
    text-align: center;

    p { padding: 0.4rem; font-size: 0.9rem; color: rgba(255,255,255,0.6); line-height: 1.5; }
`;

const Select = styled.select`
    width: 100%;
    border: 1px solid rgba(255, 255, 255, 0.08);
    padding: 0.7em 1rem;
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.06);
    color: rgba(255, 255, 255, 0.9);
    font-size: 0.9rem;
    font-family: 'Poppins', sans-serif;
    -webkit-appearance: none;
    margin: 0.5rem 0;
    transition: border-color 0.2s;

    &:focus { outline: none; border-color: rgba(99, 102, 241, 0.5); }
    & option { background: #1a1a2e; color: white; }

    @media (max-width: 768px) { padding: 1rem; }
`;

const QRCodeWrapper = styled.div`
    max-width: 74%;
    margin: 1rem auto;
    padding: 1rem;
    background: white;
    border-radius: 12px;
    display: inline-block;
`;

const ShareButton = styled.button`
    padding: 0.6rem 1.2rem;
    margin: 0.4rem;
    border: none;
    border-radius: 12px;
    background: linear-gradient(135deg, #6366f1, #8b5cf6);
    color: white;
    font-weight: 600;
    font-size: 0.85rem;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 3px 12px rgba(99, 102, 241, 0.3);
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;

    &:hover {
        transform: translateY(-1px);
        box-shadow: 0 5px 18px rgba(99, 102, 241, 0.45);
    }
    &:active { transform: translateY(0); }
`;

const ButtonContainer = styled.div`
    display: flex;
    gap: 0.6rem;
    justify-content: center;
    margin-top: 0.5rem;
`;

const ShareActionsContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
`;

const ShareButtonContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 0.4rem;
`;

const StyledLabel = styled.label`
    font-size: 0.85rem;
    color: rgba(255, 255, 255, 0.6);
    display: block;
    text-align: left;
`;

const StyledInput = styled.input`
    width: 100%;
    border: 1px solid rgba(255, 255, 255, 0.08);
    padding: 0.7rem 1rem;
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.06);
    color: rgba(255, 255, 255, 0.9);
    font-size: 0.9rem;
    font-family: 'Poppins', sans-serif;
    margin: 0.5rem 0;
    transition: border-color 0.2s, background 0.2s;

    &::placeholder { color: rgba(255, 255, 255, 0.25); }
    &:focus { outline: none; border-color: rgba(99, 102, 241, 0.5); background: rgba(255,255,255,0.08); }
`;

const ActionButton = styled.button`
    padding: 0.55rem 1.6rem;
    border-radius: 12px;
    border: none;
    background: linear-gradient(135deg, #6366f1, #8b5cf6);
    color: white;
    font-weight: 600;
    font-size: 0.85rem;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 3px 14px rgba(99, 102, 241, 0.3);

    &:hover {
        transform: translateY(-1px);
        box-shadow: 0 5px 20px rgba(99, 102, 241, 0.45);
    }
    &:active { transform: translateY(0); }
`;

const SpinnerOverlay = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(30, 27, 75, 0.85);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10;
    border-radius: 20px;
`;

const ChatInstructions = styled.p`
    text-align: left;
    font-size: 0.88rem;
    color: rgba(255,255,255,0.55);
    line-height: 1.6;
    padding: 0.5rem 0;
`;

const ShareModal = ({ inviteLink, inviteCode, onClose, onLanguageSelected }) => {
    const { t } = useTranslation();
    const [languageSelected, setLanguageSelected] = useState(null);
    const [customLanguage, setCustomLanguage] = useState(null);
    const [inviteLinkWithLang, setInviteLinkWithLang] = useState(null);
    const [chatInstructions, setChatInstructions] = useState(null);
    const [showInstructionView, setShowInstructionView] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [supportedLanguages, setSupportedLanguages] = useState([]);
    const [languageConfiguration, setLanguageConfiguration] = useState(null);

    useEffect(() => {
        fetchLanguages(setSupportedLanguages, setLanguageConfiguration);
    }, []);

    const handleCopy = () => {
        navigator.clipboard.writeText(inviteLinkWithLang).then(() => {
            alert(t('ChatShareModalCopyAlert'));
        });
    };

    const handleEmailShare = () => {
        let culture;
        if (languageConfiguration[languageSelected]?.loc) {
            culture = languageConfiguration[languageSelected].loc;
        } else {
            culture = 'en';
        }
        const translate = i18n.getFixedT(culture);
        const subject = encodeURIComponent(translate('ChatShareModalEmailSubject'));
        const body = encodeURIComponent(`${translate('ChatShareModalInstruction')}  - ${inviteLinkWithLang}`);
        window.open(`mailto:?subject=${subject}&body=${body}`);
    };

    const setupInstructionsView = async (chosenLanguage) => {
        let selectedCulture = languageConfiguration[chosenLanguage].loc;
        let instructions = languageConfiguration[chosenLanguage].instructions;
        setChatInstructions(instructions);
        setInviteLinkWithLang(`${inviteLink}?lang=${selectedCulture}&full=${chosenLanguage}`);
    };

    const checkCustomLanguage = async (language) => {
        const response = await axios.post(`${process.env.REACT_APP_SERVER}/lang/c`, { customLanguage: language }, { withCredentials: true });
        if (!response.data.success) {
            if (response.data.suggestion) {
                setErrorMessage(`Did you mean '${response.data.suggestion}'?`);
            } else {
                setErrorMessage("Sorry we don't recognize that as a language.");
            }
        }
        return response.data;
    };

    const handleContinue = async () => {
        setErrorMessage("");
        if (!languageSelected || languageSelected === 'default') {
            setErrorMessage("Please choose a language");
            return;
        }

        setIsSubmitting(true);

        if (languageSelected === 'Other') {
            if (!customLanguage) {
                setErrorMessage("Please specify a language");
            } else {
                if (supportedLanguages[customLanguage]) {
                    setLanguageSelected(customLanguage);
                    let culture = languageConfiguration[customLanguage].loc;
                    setChatInstructions(languageConfiguration[customLanguage].instructions);
                    setInviteLinkWithLang(`${inviteLink}?lang=${culture}&full=${customLanguage}`);
                    setShowInstructionView(true);
                } else {
                    const isCustomLanguageValid = await checkCustomLanguage(customLanguage);
                    if (isCustomLanguageValid.success) {
                        let defaultCulture = isCustomLanguageValid.culture;
                        let instructions;
                        if (isCustomLanguageValid.settings) {
                            instructions = isCustomLanguageValid.settings.instructions;
                        } else {
                            instructions = languageConfiguration['English'].instructions;
                        }
                        setChatInstructions(instructions);
                        setInviteLinkWithLang(`${inviteLink}?lang=${defaultCulture}&full=${customLanguage}`);
                        setShowInstructionView(true);
                    }
                }
            }
        } else {
            onLanguageSelected(languageSelected);
            await setupInstructionsView(languageSelected);
            setShowInstructionView(true);
        }

        setIsSubmitting(false);
    };

    const handleLanguageChange = (e) => {
        setLanguageSelected(e.target.value);
        setErrorMessage("");
    };

    return (
        <ModalBackground onClick={onClose}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
                {isSubmitting && <SpinnerOverlay><ClipLoader size="25px" color="#a5b4fc" /></SpinnerOverlay>}
                {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}

                {showInstructionView ? (
                    <>
                        <p>[{t('ChatShareModalInstruction')}]</p>
                        <ChatInstructions>{chatInstructions}</ChatInstructions>

                        <ShareActionsContainer>
                            <QRCodeWrapper>
                                {inviteLinkWithLang && <QRCode value={inviteLinkWithLang} size={140} />}
                            </QRCodeWrapper>
                            <ShareButtonContainer>
                                <ShareButton onClick={handleCopy}>
                                    <BsFillClipboard2PlusFill size={16} />
                                    {t('ChatShareModalCopy')}
                                </ShareButton>
                                <ShareButton onClick={handleEmailShare}>
                                    <BsFillEnvelopeAtFill size={16} />
                                    {t('ChatShareModalEmail')}
                                </ShareButton>
                            </ShareButtonContainer>
                        </ShareActionsContainer>
                    </>
                ) : (
                    <>
                        <StyledLabel>
                            <p>{t('ChatShareModalLangSelect')}</p>
                            <Select onChange={e => handleLanguageChange(e)}>
                                <option value="default">{t('LanguageSelectDefault')}</option>
                                {supportedLanguages.map((language, index) => (
                                    <option key={index} value={language}>{t(language)}</option>
                                ))}
                                <option value="Other">Other</option>
                            </Select>
                        </StyledLabel>

                        {languageSelected === 'Other' && (
                            <StyledLabel>
                                <StyledInput
                                    type="text"
                                    placeholder="Please specify the language"
                                    value={customLanguage}
                                    onChange={e => setCustomLanguage(e.target.value)}
                                    required={languageSelected === 'Other'}
                                />
                            </StyledLabel>
                        )}

                        <ButtonContainer>
                            <ActionButton onClick={handleContinue}>{t('ContinueButton')}</ActionButton>
                        </ButtonContainer>
                    </>
                )}
            </ModalContent>
        </ModalBackground>
    );
};

export default ShareModal;
